import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtMins } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { TaskRow } from "./TaskNode";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  DoorClosed,
  Loader2,
  ChevronRight,
  ListTree,
  Clock,
  Copy,
  CopyPlus,
  Power,
  Ban,
} from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);

// deep-clone a task tree with fresh ids (for cloning a room)
function cloneTasks(tasks) {
  return (tasks || []).map((t) => ({
    ...t,
    id: uid(),
    children: cloneTasks(t.children),
  }));
}

// normalise a leaf name so "Bedroom 1"/"Bedroom 2" match "Bedroom"
function normName(name) {
  return String(name || "").replace(/\s*\d+$/, "").trim().toLowerCase();
}

// map of normalised leaf name -> its time/applies/sched values
function buildTimeMap(tasks, map = {}) {
  for (const t of tasks || []) {
    if (t.children?.length) buildTimeMap(t.children, map);
    else if (!(normName(t.name) in map))
      map[normName(t.name)] = {
        est_full: t.est_full,
        est_service: t.est_service,
        applies_to: t.applies_to,
        sched: t.sched,
      };
  }
  return map;
}

// apply source times onto a target tree, matching leaves by normalised name
function applyTimes(tasks, map) {
  return (tasks || []).map((t) => {
    if (t.children?.length) return { ...t, children: applyTimes(t.children, map) };
    const src = map[normName(t.name)];
    return src ? { ...t, ...src } : t;
  });
}

function rollup(node) {
  if (node.children && node.children.length) {
    const kids = node.children.map(rollup);
    return {
      ...node,
      children: kids,
      est_full: Math.round(kids.reduce((s, k) => s + k.est_full, 0) * 100) / 100,
      est_service: Math.round(kids.reduce((s, k) => s + k.est_service, 0) * 100) / 100,
    };
  }
  return node;
}

function totals(tasks) {
  const rolled = (tasks || []).map(rollup);
  return {
    full: rolled.reduce((s, t) => s + t.est_full, 0),
    service: rolled.reduce((s, t) => s + t.est_service, 0),
  };
}

// pre-order flatten of the rolled-up tree into rows with depth
function flattenTasks(tasks, depth = 0, out = []) {
  for (const t of tasks || []) {
    const rolled = rollup(t);
    const isParent = rolled.children && rolled.children.length > 0;
    out.push({ node: rolled, depth, isParent });
    if (isParent) flattenTasks(rolled.children, depth + 1, out);
  }
  return out;
}

// immutable tree ops
function mapTree(tasks, id, fn) {
  return tasks.map((t) => {
    if (t.id === id) return fn(t);
    if (t.children?.length) return { ...t, children: mapTree(t.children, id, fn) };
    return t;
  });
}
function removeFromTree(tasks, id) {
  return tasks
    .filter((t) => t.id !== id)
    .map((t) => (t.children?.length ? { ...t, children: removeFromTree(t.children, id) } : t));
}

export default function Rooms() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // room object or {new:true}

  const { data: rooms = [], isLoading } = useQuery({
    queryKey: ["rooms"],
    queryFn: async () => (await api.get("/rooms")).data,
  });

  const setActive = useMutation({
    mutationFn: ({ id, active }) => api.patch(`/rooms/${id}/active`, { active }),
    onSuccess: (_r, v) => {
      toast.success(v.active ? "Room re-enabled" : "Room disabled — excluded from future lists");
      qc.invalidateQueries({ queryKey: ["rooms"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/rooms/${id}`),
    onSuccess: (res) => {
      if (res.data.disabled) toast.info(res.data.message);
      else toast.success("Room deleted");
      qc.invalidateQueries({ queryKey: ["rooms"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold">{t("Rooms & Tasks")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("Build hierarchical task lists. Estimated minutes roll up automatically.")}
          </p>
        </div>
        <Button
          onClick={() => setEditing({ new: true, room_number: "", room_type: "", tasks: [] })}
          data-testid="add-room-btn"
          className="k-press gap-2"
        >
          <Plus className="h-4 w-4" /> {t("Add Room")}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : rooms.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 rounded-md border border-dashed py-20 text-center">
          <ListTree className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {t("No rooms yet. Add one, or upload a CSV to auto-create room templates.")}
          </p>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="rooms-grid">
          {rooms.map((r) => (
            <Card
              key={r.id}
              data-testid={`room-card-${r.room_number}`}
              className={`rounded-md border p-4 ${r.active === false ? "opacity-60" : "hover:border-primary/40"}`}
            >
              <div
                onClick={() => setEditing(r)}
                className="k-press cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <DoorClosed className="h-4 w-4 text-muted-foreground" />
                    <span className="font-display text-lg font-extrabold">{r.room_number}</span>
                    {r.active === false && (
                      <span
                        data-testid={`room-disabled-badge-${r.room_number}`}
                        className="rounded-full border border-rose-300 bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-700"
                      >
                        {t("Disabled")}
                      </span>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">{r.room_type || "—"}</div>
                <div className="mt-3 flex gap-2 text-xs">
                  <span className="status-departing inline-flex items-center gap-1 rounded-md border px-2 py-1 font-semibold">
                    <Clock className="h-3 w-3" /> {t("Full")} {fmtMins(r.totals?.est_full)}
                  </span>
                  <span className="status-in_house inline-flex items-center gap-1 rounded-md border px-2 py-1 font-semibold">
                    <Clock className="h-3 w-3" /> {t("Service")} {fmtMins(r.totals?.est_service)}
                  </span>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 border-t border-dashed border-border pt-2.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); setActive.mutate({ id: r.id, active: r.active === false }); }}
                  data-testid={`room-toggle-active-${r.room_number}`}
                  className="h-8 gap-1.5 px-2 text-xs font-semibold"
                >
                  {r.active === false ? <><Power className="h-3.5 w-3.5" /> {t("Enable")}</> : <><Ban className="h-3.5 w-3.5" /> {t("Disable")}</>}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); del.mutate(r.id); }}
                  data-testid={`room-delete-${r.room_number}`}
                  className="h-8 gap-1.5 px-2 text-xs font-semibold text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" /> {r.has_history ? t("Remove") : t("Delete")}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <RoomEditor
          room={editing}
          allRooms={rooms}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["rooms"] });
          }}
        />
      )}
    </div>
  );
}

function RoomEditor({ room, allRooms = [], onClose, onSaved }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [roomNumber, setRoomNumber] = useState(room.room_number || "");
  const [roomType, setRoomType] = useState(room.room_type || "");
  const [tasks, setTasks] = useState(room.tasks || []);
  const [showClone, setShowClone] = useState(false);
  const [showCopy, setShowCopy] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      api.post("/rooms", { room_number: roomNumber, room_type: roomType, tasks }),
    onSuccess: () => {
      toast.success(t("Room saved"));
      onSaved();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const tot = totals(tasks);

  const addTop = () =>
    setTasks([...tasks, { id: uid(), name: "New section", est_full: 0, est_service: 0, children: [] }]);
  const addChild = (parentId) =>
    setTasks(
      mapTree(tasks, parentId, (n) => ({
        ...n,
        children: [
          ...(n.children || []),
          { id: uid(), name: "New task", est_full: 5, est_service: 3, applies_to: "both", sched: { unit: "clean", interval: 1 }, children: [] },
        ],
      }))
    );
  const updateNode = (id, patch) => setTasks(mapTree(tasks, id, (n) => ({ ...n, ...patch })));
  const removeNode = (id) => setTasks(removeFromTree(tasks, id));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="room-editor-dialog">
        <DialogHeader>
          <DialogTitle>{room.new ? t("Add Room") : t("Room {{n}}", { n: room.room_number })}</DialogTitle>
          <DialogDescription>{t("Rooms recur daily and their status is set by the housekeeping CSV. Estimated minutes roll up automatically from sub-tasks to the room total.")}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>{t("Room number")}</Label>
            <Input
              value={roomNumber}
              onChange={(e) => setRoomNumber(e.target.value)}
              placeholder="801"
              disabled={!room.new}
              data-testid="room-number-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("Room type")}</Label>
            <Input
              value={roomType}
              onChange={(e) => setRoomType(e.target.value)}
              placeholder="1 Bedroom Deluxe"
              data-testid="room-type-input"
            />
          </div>
        </div>

        <div className="flex items-center justify-between rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm">
          <span className="label-caps text-muted-foreground">{t("Rolled-up totals")}</span>
          <span className="flex gap-3 font-semibold">
            <span className="text-indigo-700">{t("Full")} {fmtMins(tot.full)}</span>
            <span className="text-lime-700">{t("Service")} {fmtMins(tot.service)}</span>
          </span>
        </div>

        <ScrollArea className="max-h-[46vh] pr-3">
          <div className="space-y-2" data-testid="task-tree">
            {tasks.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t('No tasks yet. Add a section like "Bedroom" or "Bathroom".')}
              </p>
            )}
            {flattenTasks(tasks).map(({ node, depth, isParent }) => (
              <TaskRow
                key={node.id}
                node={node}
                depth={depth}
                isParent={isParent}
                onAddChild={addChild}
                onUpdate={updateNode}
                onRemove={removeNode}
              />
            ))}
          </div>
        </ScrollArea>

        <Button variant="outline" onClick={addTop} data-testid="add-section-btn" className="gap-2">
          <Plus className="h-4 w-4" /> {t("Add section")}
        </Button>

        <DialogFooter>
          <div className="mr-auto flex gap-2">
            {!room.new && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowClone(true)}
                  data-testid="clone-room-btn"
                  className="gap-2"
                >
                  <CopyPlus className="h-4 w-4" /> {t("Clone")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCopy(true)}
                  data-testid="copy-times-btn"
                  className="gap-2"
                >
                  <Copy className="h-4 w-4" /> {t("Copy times to rooms…")}
                </Button>
              </>
            )}
          </div>
          <Button variant="outline" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={!roomNumber || save.isPending}
            data-testid="save-room-btn"
            className="gap-2"
          >
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("Save room")}
          </Button>
        </DialogFooter>
      </DialogContent>

      {showClone && (
        <CloneDialog
          tasks={tasks}
          roomType={roomType}
          existingNumbers={allRooms.map((r) => String(r.room_number))}
          onClose={() => setShowClone(false)}
          onDone={() => {
            setShowClone(false);
            qc.invalidateQueries({ queryKey: ["rooms"] });
          }}
        />
      )}
      {showCopy && (
        <CopyTimesDialog
          sourceNumber={roomNumber}
          tasks={tasks}
          rooms={allRooms.filter((r) => String(r.room_number) !== String(roomNumber))}
          onClose={() => setShowCopy(false)}
          onDone={() => {
            setShowCopy(false);
            qc.invalidateQueries({ queryKey: ["rooms"] });
          }}
        />
      )}
    </Dialog>
  );
}

function CloneDialog({ tasks, roomType, existingNumbers, onClose, onDone }) {
  const { t } = useTranslation();
  const [num, setNum] = useState("");
  const [type, setType] = useState(roomType || "");
  const dup = existingNumbers.includes(num.trim());
  const clone = useMutation({
    mutationFn: () =>
      api.post("/rooms", { room_number: num.trim(), room_type: type, tasks: cloneTasks(tasks) }),
    onSuccess: () => {
      toast.success(t("Room {{n}} created", { n: num.trim() }));
      onDone();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="clone-dialog">
        <DialogHeader>
          <DialogTitle>{t("Clone room")}</DialogTitle>
          <DialogDescription>{t("Creates a new room with a copy of this room's sub-tasks and times.")}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>{t("New room number")}</Label>
            <Input value={num} onChange={(e) => setNum(e.target.value)} placeholder="826" data-testid="clone-number-input" />
            {dup && <span className="text-xs font-medium text-destructive">{t("That room number already exists")}</span>}
          </div>
          <div className="space-y-1.5">
            <Label>{t("Room type")}</Label>
            <Input value={type} onChange={(e) => setType(e.target.value)} placeholder="1 Bedroom" data-testid="clone-type-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("Cancel")}</Button>
          <Button
            onClick={() => clone.mutate()}
            disabled={!num.trim() || dup || clone.isPending}
            data-testid="clone-confirm-btn"
            className="gap-2"
          >
            {clone.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            <CopyPlus className="h-4 w-4" /> {t("Create clone")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CopyTimesDialog({ sourceNumber, tasks, rooms, onClose, onDone }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const allSel = selected.length === rooms.length && rooms.length > 0;
  const toggle = (n) =>
    setSelected((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));
  const toggleAll = () => setSelected(allSel ? [] : rooms.map((r) => String(r.room_number)));

  const run = async () => {
    setBusy(true);
    const map = buildTimeMap(tasks);
    let ok = 0;
    for (const r of rooms.filter((r) => selected.includes(String(r.room_number)))) {
      try {
        await api.post("/rooms", {
          room_number: String(r.room_number),
          room_type: r.room_type || "",
          tasks: applyTimes(r.tasks || [], map),
        });
        ok += 1;
      } catch (e) {
        toast.error(t("Room {{n}}: {{err}}", { n: r.room_number, err: formatApiError(e.response?.data?.detail) }));
      }
    }
    setBusy(false);
    toast.success(t("Copied times to {{count}} room(s)", { count: ok }));
    onDone();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="copy-times-dialog">
        <DialogHeader>
          <DialogTitle>{t("Copy times from Room {{n}}", { n: sourceNumber })}</DialogTitle>
          <DialogDescription>
            {t("Updates matching sub-task times (by name) in the selected rooms. Their own structure is kept — e.g. two-bedroom rooms keep both bedrooms.")}
          </DialogDescription>
        </DialogHeader>
        <button
          type="button"
          onClick={toggleAll}
          data-testid="copy-select-all"
          className="k-press mb-1 w-fit text-sm font-semibold text-primary"
        >
          {allSel ? t("Clear all") : t("Select all")}
        </button>
        <ScrollArea className="max-h-[46vh] pr-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {rooms.map((r) => {
              const n = String(r.room_number);
              const on = selected.includes(n);
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => toggle(n)}
                  data-testid={`copy-target-${n}`}
                  className={`k-press flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm ${
                    on ? "border-primary bg-primary/5" : "border-border"
                  }`}
                >
                  <Checkbox checked={on} className="pointer-events-none" />
                  <span>
                    <span className="font-semibold">{n}</span>
                    {r.room_type && <span className="block text-xs text-muted-foreground">{r.room_type}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("Cancel")}</Button>
          <Button
            onClick={run}
            disabled={selected.length === 0 || busy}
            data-testid="copy-confirm-btn"
            className="gap-2"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            <Copy className="h-4 w-4" /> {t("Copy to {{count}} room(s)", { count: selected.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
